// To Do filter area (docs/spec-todo-worklists.md): type tabs as one segmented control with counts, pill dropdowns with a
// custom popover (no native select), status Open · Closed · All, sort + assignee on the right. Styles: .tdf-* in global.css.
// On narrow screens the extra filters fold into a "Filters (n)" button.
import { useEffect, useRef, useState } from 'react'

export function Segmented({ options, value, onChange, small, label }) {
  return (
    <div className={'tdf-seg' + (small ? ' tdf-seg-sm' : '')} role="tablist" aria-label={label}>
      {options.map(o => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value} className={'tdf-seg-btn' + (value === o.value ? ' on' : '')} onClick={() => onChange(o.value)}>
          {o.label}{o.count != null && <span className="tdf-seg-n">{o.count}</span>}
        </button>
      ))}
    </div>
  )
}

const Chevron = () => <svg className="tdf-chev" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
const Check = () => <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.2l2.4 2.4 4.6-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>

// A pill that opens a menu. value '' = not set (shows the label); set + clearable = light blue "Engineering ✕".
// prefix: "Sort:" style pills always show "prefix value" and are never blue.
export function PillSelect({ label, value, options, onChange, clearable = true, prefix, align = 'left' }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const out = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const esc = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', out); document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc) }
  }, [open])
  useEffect(() => { if (!open) setQ('') }, [open])
  const sel = options.find(o => String(o.value) === String(value))
  const set = clearable && value !== '' && value != null
  const shown = q ? options.filter(o => String(o.label).toLowerCase().includes(q.toLowerCase())) : options
  return (
    <div className="tdf-pill-wrap" ref={ref}>
      <span className={'tdf-pill' + (set ? ' set' : '') + (open ? ' open' : '')}>
        <button type="button" className="tdf-pill-main" aria-haspopup="listbox" aria-expanded={open} aria-label={label + (sel && (set || prefix) ? ': ' + sel.label : '')} onClick={() => setOpen(o => !o)}>
          <span className="tdf-pill-text">{prefix ? <><span className="tdf-pill-pre">{prefix}</span> {sel ? sel.label : label}</> : set && sel ? sel.label : set ? String(value) : label}</span>
          {!set && <Chevron />}
        </button>
        {set && <button type="button" className="tdf-x" aria-label={'Clear ' + label} onClick={() => { onChange(''); setOpen(false) }}>✕</button>}
      </span>
      {open && (
        <div className={'tdf-menu' + (align === 'right' ? ' right' : '')} role="listbox" aria-label={label}>
          {options.length > 8 && <input className="tdf-menu-q" autoFocus placeholder={'Find ' + label.toLowerCase() + '…'} value={q} onChange={e => setQ(e.target.value)} />}
          {shown.map(o => {
            const on = String(o.value) === String(value)
            return (
              <button key={String(o.value)} type="button" role="option" aria-selected={on} className={'tdf-opt' + (on ? ' on' : '')} onClick={() => { onChange(o.value); setOpen(false) }}>
                <span className="tdf-opt-check">{on && <Check />}</span><span className="tdf-opt-label">{o.label}</span>
              </button>
            )
          })}
          {!shown.length && <div className="tdf-empty">Nothing found</div>}
        </div>
      )}
    </div>
  )
}

// filters: { q, department, equipment, list, location, status, type }; options: { departments, components, lists, locations }
// (each [{ value, label }]); typeTabs: [{ value, label, count }]; right: sort / assignee / high-only controls
// hideStatus: History (closed items only) has no Status control
export default function TodoFilterBar({ filters, setFilter, clearAll, anyOn, typeTabs, options, showList, right, hideStatus }) {
  const [more, setMore] = useState(false)
  const extra = ['department', 'equipment', 'list', 'location'].filter(k => filters[k] && (k !== 'list' || showList)).length + (!hideStatus && filters.status !== 'open' ? 1 : 0)
  return (
    <div className="tdf">
      <div className="tdf-row">
        <Segmented label="Type" options={typeTabs} value={filters.type} onChange={v => setFilter('type', v)} />
        {anyOn && <button type="button" className="tdf-clear" onClick={clearAll}>Clear all</button>}
      </div>
      <div className="tdf-row tdf-filters">
        <label className="tdf-search">
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.8" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M10.6 10.6L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          <input placeholder="Search…" aria-label="Search" value={filters.q} onChange={e => setFilter('q', e.target.value)} />
          {filters.q && <button type="button" className="tdf-x" aria-label="Clear search" onClick={() => setFilter('q', '')}>✕</button>}
        </label>
        <span className={'tdf-pill tdf-more' + (extra ? ' set' : '')}><button type="button" className="tdf-pill-main" aria-expanded={more} onClick={() => setMore(v => !v)}>Filters{extra ? ' (' + extra + ')' : ''}<Chevron /></button></span>
        <div className={'tdf-extra' + (more ? ' open' : '')}>
          <PillSelect label="Department" value={filters.department} options={options.departments} onChange={v => setFilter('department', v)} />
          <PillSelect label="Component" value={filters.equipment} options={options.components} onChange={v => setFilter('equipment', v)} />
          {showList && <PillSelect label="List" value={filters.list} options={options.lists} onChange={v => setFilter('list', v)} />}
          <PillSelect label="Location" value={filters.location} options={options.locations} onChange={v => setFilter('location', v)} />
          {!hideStatus && <Segmented small label="Status" options={[{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }, { value: 'all', label: 'All' }]} value={filters.status} onChange={v => setFilter('status', v)} />}
        </div>
        <div className="tdf-right">{right}</div>
      </div>
    </div>
  )
}
